import { useEffect, useState } from "react";
import { Gift, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";

// Compact top-of-dashboard callout. Sits above the fold so new users
// discover the referral program the moment they land. Deliberately
// simpler than the full `ReferralCard` further down — one line, one CTA
// that scrolls to the full card (no route change, no modal — the goal is
// to nudge, not interrupt).
//
// Hidden automatically when the user is on a paid plan tier AND has no
// pending discounts to redeem, since referral rewards only apply to their
// next PAID purchase — those users already got the value prop.
export default function ReferralBanner({ isPaidPlan = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        setData(data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!data) return null;
  // Suppress on paid plans unless they have discounts ready to redeem.
  if (isPaidPlan && data.discounts_available === 0) return null;

  const scrollToCard = () => {
    const el = document.querySelector("[data-testid='referral-card']");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const hasReward = data.discounts_available > 0;

  return (
    <div
      data-testid="referral-banner"
      className={
        "border p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-6 " +
        (hasReward
          ? "border-emerald-500/40 bg-emerald-500/5"
          : "border-blue-500/30 bg-blue-500/5")
      }
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={
            "w-9 h-9 shrink-0 grid place-items-center rounded-full " +
            (hasReward ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300")
          }
        >
          {hasReward ? <Sparkles className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
        </div>
        <div className="min-w-0">
          <div
            data-testid="referral-banner-message"
            className="font-semibold text-sm text-white leading-tight"
          >
            {hasReward
              ? `You've earned ${data.discounts_available} × ${data.discount_percent}% reward — one per eligible purchase, no stacking.`
              : `Refer ${data.progress_target} friends, earn ${data.discount_percent}% off your eligible purchase — share your link.`}
          </div>
          {!hasReward && (
            <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
              Progress: {data.progress} / {data.progress_target} · new users get {data.free_minutes} free minutes once. Referred buyers pay the advertised price.
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={scrollToCard}
        data-testid="referral-banner-cta"
        className={
          "shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-3 py-2 border transition-colors duration-150 " +
          (hasReward
            ? "border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
            : "border-blue-500/40 text-blue-200 hover:bg-blue-500/10")
        }
      >
        {hasReward ? "See details" : "Share your link"}
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
